
import { Helmet } from "react-helmet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Info } from "lucide-react";

const LainavertailuPage = () => {
  return (
    <>
      <Helmet>
        <title>Lainavertailu - Vertaile eri lainoja | Lainasampo.fi</title>
        <meta name="description" content="Vertaile eri lainojen korkoja, ehtoja ja ominaisuuksia. Löydä itsellesi sopivin laina vertailemalla tarjouksia." />
      </Helmet>
      
      <div className="min-h-screen bg-white">
        {/* Hero section with background image */}
        <div className="relative overflow-hidden bg-blue-700 py-16 mb-12">
          <div className="absolute inset-0 z-0 opacity-20">
            <img 
              src="https://images.unsplash.com/photo-1560520031-3a4dc4e9de0c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2073&q=80" 
              alt="Tyytyväiset asiakkaat tarkastelevat lainatarjouksia" 
              className="w-full h-full object-cover"
            />
          </div>
          <div className="container relative z-10 mx-auto px-6 max-w-4xl">
            <div className="max-w-2xl">
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Lainojen vertailu on nyt helpompaa kuin koskaan
              </h1>
              <p className="text-lg md:text-xl text-blue-100 mb-8">
                Vertailemalla lainatarjouksia voit säästää tuhansia euroja lainan kokonaiskustannuksissa. 
                Täytä yksi hakemus ja saat useita tarjouksia vertailtavaksi.
              </p>
              <Link to="/kilpailuta">
                <Button size="lg" className="bg-white hover:bg-blue-50 text-blue-700 font-medium px-8">
                  Kilpailuta lainasi nyt
                </Button>
              </Link>
            </div>
          </div>
        </div>
        
        <div className="container mx-auto max-w-4xl px-6">
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-blue-700">Miten lainojen vertailu toimii?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="h-full">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg text-blue-700">1. Täytä yksi hakemus</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700">
                    Kerro meille perustiedot ja minkälaista lainaa etsit. Täytät vain yhden hakemuksen, joka välitetään useille lainanantajille.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="h-full">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg text-blue-700">2. Saat useita tarjouksia</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700">
                    Lainanantajat käsittelevät hakemuksesi ja tekevät sinulle tarjouksen. Saat lainatarjoukset sähköpostiisi tai näet ne palvelussamme.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="h-full">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg text-blue-700">3. Vertaile ja valitse</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700">
                    Vertaile tarjouksia rauhassa ja valitse itsellesi sopivin lainatarjous. Hyväksy tarjous sähköisellä allekirjoituksella.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
          
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-blue-700">Lainatyyppien vertailu</h2>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[200px]">Lainatyyppi</TableHead>
                    <TableHead>Korko</TableHead>
                    <TableHead>Tyypillinen summa</TableHead>
                    <TableHead>Laina-aika</TableHead>
                    <TableHead>Soveltuvuus</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">Kulutusluotto</TableCell>
                    <TableCell>6,9 - 20,0 %</TableCell>
                    <TableCell>1 000 - 25 000 €</TableCell>
                    <TableCell>1 - 10 vuotta</TableCell>
                    <TableCell>Kodin hankinnat, remontit, lomat</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Asuntolaina</TableCell>
                    <TableCell>2,5 - 4,5 %</TableCell>
                    <TableCell>50 000 - 300 000 €</TableCell>
                    <TableCell>10 - 30 vuotta</TableCell>
                    <TableCell>Asunnon ostaminen</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Autolaina</TableCell>
                    <TableCell>5,5 - 9,0 %</TableCell>
                    <TableCell>5 000 - 50 000 €</TableCell>
                    <TableCell>1 - 7 vuotta</TableCell>
                    <TableCell>Auton hankinta</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Yhdistelylaina</TableCell>
                    <TableCell>5,9 - 14,0 %</TableCell>
                    <TableCell>2 000 - 70 000 €</TableCell>
                    <TableCell>1 - 15 vuotta</TableCell>
                    <TableCell>Useiden lainojen yhdistäminen</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Pikalaina</TableCell>
                    <TableCell>10,0 - 20,0 %</TableCell>
                    <TableCell>100 - 2 000 €</TableCell>
                    <TableCell>1 - 12 kk</TableCell>
                    <TableCell>Lyhytaikaiset, akuutit tarpeet</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
            <p className="text-sm text-gray-500 mt-2 flex items-center">
              <Info className="h-4 w-4 mr-1" /> Korot ovat suuntaa-antavia ja voivat vaihdella luottoluokituksen ja markkinatilanteen mukaan.
            </p>
          </div>
          
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-blue-700">Mitä kannattaa vertailla?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-blue-700">Korko ja kulut</CardTitle>
                  <CardDescription>Tärkeimmät kustannustekijät</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                      <span>Todellinen vuosikorko (sisältää koron ja pakolliset kulut)</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                      <span>Kuukausierän suuruus</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                      <span>Avausmaksu ja muut kertaluonteiset kulut</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                      <span>Tilinhoitomaksut ja muut kuukausittaiset kulut</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="text-blue-700">Lainaehdot</CardTitle>
                  <CardDescription>Joustavuus ja muut ehdot</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                      <span>Lyhennystapa (annuiteetti, tasalyhennys, tasaerä)</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                      <span>Mahdollisuus maksutonta lyhennykseen</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                      <span>Lainan ennenaikaisen takaisinmaksun ehdot ja kulut</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                      <span>Mahdollisuus lyhennysvapaaseen</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
          
          <div className="mb-12">
            <div className="bg-blue-50 p-8 rounded-xl">
              <h2 className="text-2xl font-bold mb-4 text-blue-700">Miksi vertailla lainoja?</h2>
              <p className="text-gray-700 mb-6">
                Lainojen vertailu voi säästää sinulle tuhansia euroja. Esimerkki: 20 000 euron laina 5 vuoden maksuajalla:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <h3 className="text-lg font-semibold text-red-600 mb-2">Ilman vertailua</h3>
                  <p className="text-4xl font-bold text-gray-900 mb-2">10,5%</p>
                  <p className="text-gray-600 mb-4">Todellinen vuosikorko</p>
                  <div className="border-t border-gray-200 pt-4">
                    <p className="text-gray-700 mb-1">Kuukausierä: <span className="font-semibold">432 €</span></p>
                    <p className="text-gray-700">Kokonaiskustannus: <span className="font-semibold">25 920 €</span></p>
                  </div>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow-sm border-2 border-green-500">
                  <h3 className="text-lg font-semibold text-green-600 mb-2">Vertailun jälkeen</h3>
                  <p className="text-4xl font-bold text-gray-900 mb-2">6,9%</p>
                  <p className="text-gray-600 mb-4">Todellinen vuosikorko</p>
                  <div className="border-t border-gray-200 pt-4">
                    <p className="text-gray-700 mb-1">Kuukausierä: <span className="font-semibold">395 €</span></p>
                    <p className="text-gray-700">Kokonaiskustannus: <span className="font-semibold">23 700 €</span></p>
                  </div>
                </div>
              </div>
              
              <div className="bg-green-100 p-4 rounded-lg">
                <p className="text-green-700 font-medium text-center">
                  Säästö lainan kokonaiskustannuksissa: <span className="font-bold">2 220 €</span>
                </p>
              </div>
            </div>
          </div>
          
          <div className="mb-12 text-center">
            <h2 className="text-2xl font-bold mb-6 text-blue-700">Aloita lainatarjousten vertailu nyt</h2>
            <p className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto">
              Täytä yksi hakemus ja vertaile tarjouksia yli 30 pankista ja rahoituslaitoksesta. 
              Palvelumme on täysin maksuton eikä sido sinua mihinkään.
            </p>
            <Link to="/kilpailuta">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-8 py-6 text-lg">
                Kilpailuta lainasi nyt
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
          
          <div className="mb-16">
            <h2 className="text-2xl font-bold mb-6 text-blue-700">Usein kysytyt kysymykset</h2>
            <div className="space-y-4">
              <details className="group rounded-lg border border-gray-200 p-4">
                <summary className="flex cursor-pointer items-center justify-between font-medium">
                  <span>Maksaako lainojen vertailu jotain?</span>
                  <span className="transition group-open:rotate-180">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                    </svg>
                  </span>
                </summary>
                <div className="mt-4 text-gray-700">
                  <p>Ei, lainojen vertailu Lainasampo.fi:n kautta on täysin maksutonta. Emme veloita asiakkailtamme mitään palvelun käytöstä.</p>
                </div>
              </details>
              
              <details className="group rounded-lg border border-gray-200 p-4">
                <summary className="flex cursor-pointer items-center justify-between font-medium">
                  <span>Tekeekö vertailu luottotietomerkinnän?</span>
                  <span className="transition group-open:rotate-180">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                    </svg>
                  </span>
                </summary>
                <div className="mt-4 text-gray-700">
                  <p>Vertailun tekeminen ei itsessään aiheuta luottotietomerkintää. Merkintä voi tulla vain, jos et hoida lainaa sovitusti. Lainanantajat kuitenkin tarkistavat luottotietosi hakemuksen käsittelyn yhteydessä.</p>
                </div>
              </details>
              
              <details className="group rounded-lg border border-gray-200 p-4">
                <summary className="flex cursor-pointer items-center justify-between font-medium">
                  <span>Kuinka kauan tarjousten saaminen kestää?</span>
                  <span className="transition group-open:rotate-180">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                    </svg>
                  </span>
                </summary>
                <div className="mt-4 text-gray-700">
                  <p>Useimmissa tapauksissa saat ensimmäiset tarjoukset jo saman päivän aikana. Kaikki tarjoukset ovat saatavilla tyypillisesti 1-2 arkipäivän kuluessa hakemuksen jättämisestä.</p>
                </div>
              </details>
              
              <details className="group rounded-lg border border-gray-200 p-4">
                <summary className="flex cursor-pointer items-center justify-between font-medium">
                  <span>Sitoudunko mihinkään tehdessäni lainahakemuksen?</span>
                  <span className="transition group-open:rotate-180">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                    </svg>
                  </span>
                </summary>
                <div className="mt-4 text-gray-700">
                  <p>Et sitoudu mihinkään täyttäessäsi hakemuksen. Saat tarjoukset tarkasteltavaksi ja päätät itse, haluatko hyväksyä jonkin tarjouksen vai et.</p>
                </div>
              </details>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LainavertailuPage;
